import { Maybe } from '../../common/types'

interface AddressParts {
  name: Maybe<string>
  address: Maybe<string>
  city: Maybe<string>
  streetNo: Maybe<string>
  postalCode: Maybe<string>
}

type ParsedAddress = {
  zipcode: string
  city: string
  address: string
  name: string
}

export function findComponent(component: google.maps.GeocoderResult, type: string) {
  for (let i = 0; i < component.address_components.length; i++) {
    if (component.address_components[i].types.indexOf(type) !== -1) {
      return component.address_components[i]
    }
  }
  return null
}

export function parsePlaceAddressComponents(
  name: string,
  addressComponents: google.maps.places.AddressComponent[],
  includeName: boolean = true
): ParsedAddress {
  const components = addressComponents

  const addressParts = {
    name: name || '',
    address: '',
    city: '',
    streetNo: '',
    postalCode: '',
  }

  const getText = (element: google.maps.places.AddressComponent) => {
    return element.longText || ''
  }

  components.forEach((element) => {
    if (element.types.includes('route')) {
      addressParts.address = getText(element)
    }
    if (element.types.includes('locality') || element.types.includes('administrative_area_level_3')) {
      addressParts.city = getText(element)
    }
    if (element.types.includes('street_number')) {
      addressParts.streetNo = getText(element)
    }
    if (element.types.includes('postal_code')) {
      addressParts.postalCode = getText(element)
    }
  })

  if (addressParts.address && addressParts.streetNo) {
    addressParts.address = combineAddressAndStreetNo([addressParts.address, addressParts.streetNo])
  }
  if (addressParts.name === addressParts.address) {
    addressParts.name = ''
  }
  if (addressParts.name && addressParts.address !== addressParts.name && includeName) {
    addressParts.address = combineLocationString([addressParts.name, addressParts.address || ''])
  }

  return { ...addressParts, zipcode: addressParts.postalCode}
}

export const parseAddressComponents = (address: google.maps.GeocoderResult): string[] => {
  const components = address?.address_components || []

  const addressParts: AddressParts = {
    name:  null,
    address: null,
    city: null,
    streetNo: null,
    postalCode: null,
  }

  components.forEach((element: google.maps.GeocoderAddressComponent) => {
    if (element.types.includes('route')) {
      addressParts.address = element.long_name
    }
    if (element.types.includes('locality') || element.types.includes('administrative_area_level_3')) {
      addressParts.city = element.long_name
    }
    if (element.types.includes('street_number')) {
      addressParts.streetNo = element.long_name
    }
    if (element.types.includes('postal_code')) {
      addressParts.postalCode = element.long_name
    }
  })

  if (addressParts.address && addressParts.streetNo) {
    addressParts.address = combineAddressAndStreetNo([addressParts.address, addressParts.streetNo])
  }
 
  return [addressParts.address || '', addressParts.city || '', addressParts.postalCode || '']
}

const combineLocationString = (entries: Array<string | null>) =>
  entries
    .filter(name => name)
    .map(name => name && name.trim())
    .filter(name => name)
    .join(', ')

const combineAddressAndStreetNo = (entries: Array<string | null>) =>
  entries
    .filter(name => name)
    .map(name => name && name.trim())
    .filter(name => name)
    .join(' ')

export function formatAutoSuggestionResult(suggestions: google.maps.places.AutocompleteSuggestion[]) {
  return suggestions.map(suggestion => ({
    main:
      suggestion.placePrediction && suggestion.placePrediction.mainText ? suggestion.placePrediction.mainText.text : '',
    secondary:
      suggestion.placePrediction && suggestion.placePrediction.secondaryText
        ? suggestion.placePrediction.secondaryText.text
        : '',
    placeId: suggestion.placePrediction && suggestion.placePrediction.placeId ? suggestion.placePrediction.placeId : '',
  }))
}
